// src/components/DataCenter/PeriodFilter.js
import React, { useEffect, useRef, useState } from "react";
import "./PeriodFilter.css";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import DateFilterBar from "./DateFilterBar";

function PeriodFilter({ onChange }) {
  const { primaryRestaurantId } = useAuth();
  const location = useLocation();
  const sentinelRef = useRef(null);
  const filterRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);

  // URL pattern: /data/2/4  -> categoryID = 2, chartID = 4
  const segments = location.pathname.split("/").filter(Boolean);
  let categoryID = null;
  let chartID = null;

  const dataIndex = segments.indexOf("data");
  if (dataIndex !== -1) {
    categoryID = segments[dataIndex + 1] || null;
    chartID = segments[dataIndex + 2] || null;
  }

  const storageKey = `date-${primaryRestaurantId || "none"}-cat${
    categoryID || "0"
  }-q${chartID || "0"}`;

  const handleFilterChange = ({ mode, start, end }) => {
    onChange({ mode, start, end });
  };

  // Pin to center-top once the sentinel scrolls above the viewport
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldPin =
          !entry.isIntersecting && entry.boundingClientRect.top < 0;
        setPinned(shouldPin);
        if (!shouldPin) setMinimized(false);
      },
      { threshold: [0, 1], rootMargin: "0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Keep layout height when switching to fixed so the page doesn't jump
  useEffect(() => {
    if (!filterRef.current) return;
    if (pinned) {
      setPlaceholderHeight(filterRef.current.offsetHeight);
    } else {
      setPlaceholderHeight(0);
    }
  }, [pinned, minimized]);

  useEffect(() => {
    if (!pinned || !filterRef.current) return;
    const el = filterRef.current;
    const sync = () => setPlaceholderHeight(el.offsetHeight);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pinned, minimized]);

  const pinnedClass = [
    "chart-period-filter",
    pinned ? "is-pinned" : "",
    pinned && minimized ? "is-minimized" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div
        ref={sentinelRef}
        className="chart-period-filter-sentinel"
        aria-hidden
      />
      {pinned && (
        <div
          className="chart-period-filter-placeholder"
          style={{ height: placeholderHeight }}
          aria-hidden
        />
      )}
      <div ref={filterRef} id="chart-period-filter" className={pinnedClass}>
        {pinned && (
          <div className="chart-period-filter-pin-bar">
            <span className="chart-period-filter-pin-title">Filter Data</span>
            <button
              type="button"
              className="chart-period-filter-min-btn"
              onClick={() => setMinimized((v) => !v)}
              aria-expanded={!minimized}
              aria-label={
                minimized
                  ? "Expand date filter"
                  : "Minimize date filter"
              }
            >
              {minimized ? "Expand" : "Minimize"}
            </button>
          </div>
        )}
        {!(pinned && minimized) && (
          <DateFilterBar
            onChange={handleFilterChange}
            storageKey={storageKey}
          />
        )}
      </div>
    </>
  );
}

export default PeriodFilter;
